/**
@format
@flow local-strict
*/
import React, { useState } from 'react';
import { Auth } from 'aws-amplify';
import { useForm, Controller } from 'react-hook-form';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import BannerNotification from '@ziynx/components/notifications';
import { Button } from '@ziynx/components/buttons';
import Colors from '@ziynx/theme/colors';
import Input from '@ziynx/components/inputs';
import { withAppManager, emailRegex } from '@ziynx/components/utilities';
import { Body } from '@ziynx/components/typography';
import { ContentContainer } from '@ziynx/components/layout';
import { Form, FormRow } from '@ziynx/components/forms';
import Link from '@ziynx/components/links';
import Image from '@ziynx/components/images';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import logo from '../../assets/ZIYNX_LOGO_2.png';
import logoSmall from '../../assets/ZIYNX_SYMBOL_2 2x.png';

type Props = $ReadOnly<{|
  /**
   * Navigation stack
   */
  history: Object,

|}>;

// Replace with card ??
const FormSectionWrapper = styled.div`
  display: table;
  margin: 0 auto;
  ${({ viewport }) => (viewport === 'mobileSmall' || viewport === 'mobile') && `
    padding-left: 16px;
    padding-right: 16px;
    padding-bottom: 34px;
`};
`;

const StyledBannerWrapper = styled.div`
  z-index: 996;
  width: 100%;
  height: 0;
`;

const StyledBodyWrapper = styled.div`
  ${({ lastChild }) => !lastChild && ' padding-bottom: 40px;'};
  ${({ center }) => center && 'text-align: center;'}
`;

const FormSubTitle = styled(StyledBodyWrapper)`
  max-width: 480px;
  padding-bottom: 48px;
`;

const StyledButton = styled(Button)`
  margin-bottom: 16px;
`;

const ImageWrapper = styled.div`
  padding-bottom: 64px;
  padding-top: ${({ viewport }) => ((viewport === 'mobile' || viewport === 'mobileSmall' || viewport === 'tablet') ? 32 : 48)}px;
`;

/**
 * Object to hold data for each types of inputs to
 * be used in Controller
 */
const inputsData = {
  data: [
    {
      id: '1',
      name: 'email',
      defaultValue: '',
      regExPattern: emailRegex,
      errorMessage: 'Please enter a valid email address',
    },
  ],
};

/**
 * Time for banner to setTimeout
 */
const bannerTimeOut = 5000;

/**
 * Object to store common error messages for banner notification
 */
const bannerMessageData = {
  accountNotFoundError: `We couldn't find an account matching the email and password
  you entered. Please check your email and password again`,
};

/**
 * Confirm email screen to send user a verification code
 * to continue with the reset password process
 */
const ConfirmEmailScreen = (props: ManagedProps<Props>): React$Node => {
  const { history, viewport } = props;
  const {
    handleSubmit, control, errors, formState, clearErrors, setError,
  } = useForm();

  // Fields to watch for changes or errors
  const { dirtyFields } = formState;

  // state for banner error message
  const [bannerErrorMessage, setBannerErrorMessage] = useState('');
  // State to keep track if banner should be rendered
  const [shouldRenderBanner, setRenderBanner] = useState(false);
  // Shows if waiting for response from AWS
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Function to handle when user clicks button. Confirm code will
   * be sent to email or via SMS
   * @see https://docs.aws.amazon.com/cognito-user-identity-pools/latest/APIReference/API_ForgotPassword.html for errors
   */
  const onSubmit = async (data: Object): Promise<any> => {
    setIsLoading(true);
    const { email } = data || {};
    const trimmedEmail = email?.trim();
    try {
      await Auth.forgotPassword(trimmedEmail);

      history.push('/reset-password', { email: trimmedEmail });
    } catch (error) {
      setIsLoading(false);
      setRenderBanner(false);
      switch (error.code) {
      case 'UserNotConfirmedException':
        // When user is not properly confirmed upon sign up
        // Routes to confirm-code to confirm email first
        history.push('/confirm-code');
        break;

      case 'UserNotFoundException':
      case 'NotAuthorizedException':
        setError('email', {
          type: 'manual',
          message: 'Invalid email address',

        });
        setRenderBanner(true);
        setBannerErrorMessage(bannerMessageData.accountNotFoundError);
        break;

      case 'CodeDeliveryFailureException':
      default:
        setRenderBanner(true);
        setBannerErrorMessage(error.message);
        break;
      }
    }
  };

  /**
   * Function to render Inputs for Controller render prop
   * of useForm hook.
   * Using this function just in case more inputs are added
   * later on
   */
  const renderInputs = ({ onChange, onFocus, name }): React$Node => {
    let label = '';
    let variant = 'text'; // default type of inputs

    switch (name) {
    case 'email':
      variant = 'email';
      label = 'Email';
      break;
    default:
      label = 'Email';
      break;
    }

    /**
     * Sets the value of input and if accountNotFoundError is true, set to false
     * so user can start typing into the inputs again after errors
     */
    const onInputChange = (event: SyntheticInputEvent<HTMLInputElement>): void => {
      (event?.target: HTMLInputElement);
      onChange(event?.target?.value);
    };

    /**
     * Function to clear any errors of inputs
     * when on focus event, if any
     */
    const onInputFocus = (): any => {
      if (errors?.[name]) clearErrors(name);
      return onFocus;
    };

    return (
      <Input
        variant={variant}
        error={!!errors?.[name]}
        errorLabel={errors?.[name]?.message}
        inputName={name}
        label={label}
        onChange={onInputChange}
        onFocus={onInputFocus}
        required
      />
    );
  };

  /**
   * Function to map out FormRows
   * that holds the Controller of each types of inputs
   * based on inputsData
   */
  const renderInputsControllers = (): React$Node => inputsData.data.map((field) => (
    <FormRow key={field.id} paddingBottom="40px">
      <Controller
        control={control}
        render={renderInputs}
        name={field.name}
        defaultValue={field.defaultValue}
        rules={{
          required: 'Please enter your email address to continue',
          pattern: {
            value: field?.regExPattern,
            message: field?.errorMessage,
          },
        }}
      />
    </FormRow>
  ));

  /**
   * Function to check if dirtyFields length equals to inputsData length.
   * Returns true if not -> disable button
   */
  const isDisabled = (): boolean => {
    if (!inputsData?.data || !dirtyFields) return true;
    return Object.keys(dirtyFields).length !== inputsData?.data?.length;
  };

  /**
   * Function to watch if an input has error
   * after user submit upon validation.
   * Errors object is {} by default - which means no error
   * Returns true if length of errors object != 0
   * and false if empty
   */
  const isError = (): boolean => !!Object.keys(errors).length;

  /**
   * To render ziynx logo as image
   */
  const renderLogo = (): React$Node => {
    if (viewport !== 'desktop' && viewport !== 'desktopSmall') {
      return (
        <Image src={logoSmall} width="24px" height="24px" alt="company-logo" />
      );
    }
    return (
      <Image src={logo} width="114px" height="24px" alt="company-logo" />
    );
  };

  return (
    <>
      <Helmet>
        <title>Reset Password | ZIYNX</title>
      </Helmet>
      <ContentContainer offsetTop={0} isVertical>
        {shouldRenderBanner && (
          <StyledBannerWrapper>
            <BannerNotification animated timeout={bannerTimeOut} variant="error">
              {bannerErrorMessage}
            </BannerNotification>
          </StyledBannerWrapper>
        )}
        <FormSectionWrapper viewport={viewport} hasBanner={shouldRenderBanner}>
          <ImageWrapper viewport={viewport}>
            {renderLogo()}
          </ImageWrapper>
          <Form
            formID="confirm-email"
            title="Reset your password"
          >
            <FormSubTitle>
              <Body medium color={Colors.GrayDark}>
                Enter the email associated with your account. We will email you
                a code to reset your password.
              </Body>
            </FormSubTitle>

            {renderInputsControllers()}

            <StyledButton
              width="100%"
              disabled={isDisabled() || isError()}
              isLoading={isLoading}
              type="submit"
              form="confirm-email"
              onClick={handleSubmit(onSubmit)}
            >
              Send code
            </StyledButton>
            <StyledBodyWrapper center lastChild>
              <Link
                variant="navigation"
                activeOnlyWhenExact
                label="Back to Log in"
                to="/"
              />
            </StyledBodyWrapper>
          </Form>
        </FormSectionWrapper>
      </ContentContainer>
    </>
  );
};

ConfirmEmailScreen.defaultProps = {
  viewport: 'desktop',
};

export default (withAppManager(ConfirmEmailScreen): React$ComponentType<ManagedProps<Props>>);
